import type {
  AmmSpecificQuote,
  NftMetadata,
  ProtocolSnapshot,
} from "@stonkland/protocol/api-types";
import type { Address } from "viem";
import { BrokerArtwork } from "@web/features/terminal/BrokerArtwork";
import { BrokerTraitPanel } from "@web/features/terminal/BrokerTraitPanel";
import { BuyLandPanel } from "@web/features/terminal/BuyLandPanel";
import { amountLabel } from "@web/features/terminal/presentation";

type BrokersViewProps = {
  account?: Address;
  ammPrice?: bigint;
  busy?: string;
  landBalance?: bigint;
  metrics?: NonNullable<ProtocolSnapshot["metrics"]>;
  protocolLive: boolean;
  selectedToken?: bigint;
  snipeMetadata?: NftMetadata;
  snipeQuote?: AmmSpecificQuote;
  snipeToken: string;
  onBuy: (tokenId?: bigint) => Promise<void>;
  onBuyLand: (options: {
    amountIn: bigint;
    slippageBps: number;
  }) => Promise<boolean>;
  onSellLand: (options: {
    amountIn: bigint;
    slippageBps: number;
  }) => Promise<boolean>;
  onSell: () => Promise<void>;
  onSnipeToken: (value: string) => void;
  onValidationError: (message: string) => void;
};

export function BrokersView({
  account,
  ammPrice,
  busy,
  landBalance,
  metrics,
  protocolLive,
  selectedToken,
  snipeMetadata,
  snipeQuote,
  snipeToken,
  onBuy,
  onBuyLand,
  onSell,
  onSnipeToken,
  onSellLand,
  onValidationError,
}: BrokersViewProps) {
  const snipe = () => {
    const tokenId = Number(snipeToken);
    if (!Number.isSafeInteger(tokenId) || tokenId < 1 || tokenId > 4_444) {
      onValidationError("Enter a Broker ID from 1 to 4,444.");
      return;
    }
    void onBuy(BigInt(tokenId));
  };

  return (
    <div className="brokers-layout">
      <BuyLandPanel
        account={account}
        busy={busy}
        landBalance={landBalance}
        protocolLive={protocolLive}
        onBuyLand={onBuyLand}
        onSellLand={onSellLand}
      />

      <aside className="wide-panel panel amm-panel" id="buy-broker">
        <div className="amm-copy">
          <span className="section-kicker">BROKER MARKET</span>
          <h3>BUY A BROKER WITH LAND.</h3>
          <p>
            All 4,444 Brokers are reserved here—there is no public or free mint.
            Your paid AMM purchase mints an unclaimed Broker directly to you for
            333,333 LAND. Exact-ID buys and selling are kept below as advanced
            actions.
          </p>
        </div>
        <div className="transaction-breakdown amm-stats">
          <span>
            <small>LAND EXCHANGE UNIT</small>
            <strong>
              {ammPrice === undefined
                ? "—"
                : `${amountLabel(ammPrice, 0)} LAND`}
            </strong>
          </span>
          <span>
            <small>RANDOM BUY / SELL FEE</small>
            <strong>
              {metrics
                ? `${amountLabel(BigInt(metrics.ammSwapFee), 5)} ETH · ${metrics.ammRandomFeeBps / 100}%`
                : "—"}
            </strong>
          </span>
          <span>
            <small>EXACT-ID FEE</small>
            <strong>
              {metrics
                ? `${amountLabel(BigInt(metrics.ammSnipeFee), 5)} ETH · ${metrics.ammSpecificFeeBps / 100}%`
                : "—"}
            </strong>
          </span>
          <span>
            <small>ETH FEE ROUTING</small>
            <strong>70% REWARDS · 30% TREASURY</strong>
          </span>
        </div>
        <div className="amm-actions">
          <button
            className="ticket-button"
            onClick={() => void onBuy()}
            disabled={
              !protocolLive || !account || metrics?.ammInventory === "0"
            }
          >
            {busy === "buy-amm" ? "SWAPPING…" : "BUY NEXT BROKER"}
          </button>
          <details className="advanced-amm-actions">
            <summary>Choose an exact ID or sell a Broker</summary>
            <label className="field snipe-field">
              <span>EXACT BROKER ID</span>
              <input
                value={snipeToken}
                onChange={(event) => onSnipeToken(event.target.value)}
                inputMode="numeric"
                placeholder="Broker ID from 1 to 4,444"
                aria-label="Exact Broker ID"
              />
              <b>{snipeToken ? `#${snipeToken.padStart(4, "0")}` : "—"}</b>
            </label>
            <button
              className="secondary-ticket"
              disabled={
                !protocolLive || !snipeToken || !snipeQuote?.available
              }
              onClick={snipe}
            >
              {busy === "snipe" ? "BUYING…" : "BUY EXACT BROKER"}
            </button>
            <div className="advanced-sell-row">
              <span>
                {selectedToken
                  ? `Selected Broker #${selectedToken.toString().padStart(4, "0")}`
                  : "Select a Broker under Put to Work first"}
              </span>
              <button
                className="secondary-ticket"
                onClick={() => void onSell()}
                disabled={!protocolLive || !selectedToken}
              >
                {busy === "sell-amm" ? "SELLING…" : "SELL TO PROTOCOL"}
              </button>
            </div>
          </details>
        </div>
        {snipeMetadata && (
          <div className="snipe-preview">
            <div className="snipe-art">
              <BrokerArtwork tokenId={Number(snipeToken)} />
            </div>
            <div className="snipe-copy">
              <span>EXACT-ID PREVIEW</span>
              <strong>{snipeMetadata.name}</strong>
              <b className={snipeQuote?.available ? "positive" : "negative"}>
                {snipeQuote
                  ? snipeQuote.available
                    ? "AVAILABLE FROM PROTOCOL"
                    : "NOT IN PROTOCOL INVENTORY"
                  : metrics
                    ? "CHECKING LIVE INVENTORY…"
                    : "LIVE INVENTORY UNAVAILABLE"}
              </b>
              {snipeQuote && (
                <small>
                  {amountLabel(BigInt(snipeQuote.tokenCost), 0)} LAND +{" "}
                  {amountLabel(BigInt(snipeQuote.nativeFee), 5)} ETH
                </small>
              )}
            </div>
            <BrokerTraitPanel
              metadata={snipeMetadata}
              title="EXACT-ID TRAITS"
            />
          </div>
        )}
      </aside>

    </div>
  );
}
